/**
 * Phase 9-C — Notifications API client.
 *
 * Thin fetch wrapper matching `billing-api.ts` /
 * `slack-api.ts` conventions. RTK Query is intentionally NOT used — kept
 * consistent until Phase 6 (now deferred) unifies everything.
 *
 * Auth header: `Authorization: Bearer ${state.auth.agency.accessToken}`.
 * The token lives in Redux ONLY (never localStorage).
 *
 * Endpoints (Phase 9-B backend):
 *   GET    /api/v1/shops/:slug/notifications
 *   GET    /api/v1/shops/:slug/notifications/:platform/install-url
 *   POST   /api/v1/shops/:slug/notifications/:platform/manual
 *   DELETE /api/v1/shops/:slug/notifications/:id
 *   POST   /api/v1/shops/:slug/notifications/:id/test
 *
 * Secrets in `config` (bot_token, app_password, service_account_json,
 * resend_api_key) are NEVER returned by the API — the backend strips them
 * from the response payload. They are write-only inputs.
 */
import { CENTRAL_API_BASE_URL } from '../../constants/central-api';
import store from '../../../store/store';

// ---------- shared types ----------

export type ChannelType =
  | 'slack'
  | 'teams'
  | 'gchat'
  | 'email'
  | 'webhook'
  | 'discord';

export type NotificationEventType = 'proposed' | 'approved' | 'denied';

/**
 * Phase 10-A — per-channel delivery mode.
 *   - immediate: one message per proposal (with burst-protection on backend).
 *   - digest:    bundled rollup every `digest_interval_hours` (1-168).
 *   - silent:    no notifications.
 */
export type NotificationMode = 'immediate' | 'digest' | 'silent';

export interface NotificationChannel {
  id: number;
  shop_id: number;
  channel_type: ChannelType;
  external_id: string | null;
  config: Record<string, unknown>;
  event_filter: NotificationEventType[] | null;
  is_active: boolean;
  is_legacy: boolean;
  created_at: string;
  updated_at: string;
  // Phase 10-A: per-channel delivery mode.
  notification_mode: NotificationMode;
  digest_interval_hours: number;
  digest_min_items: number;
  last_digest_sent_at: string | null;
}

export interface InstallUrlResponse {
  install_url?: string;
  instructions_url?: string;
  manual: boolean;
}

export interface TestResult {
  success: boolean;
  message_id?: string;
  error?: string;
}

/**
 * Microsoft Teams — connection-code linking response.
 *
 * `POST /notifications/teams/link-code` returns a short-lived code the agency
 * pastes inside Teams (after @mentioning the ADUP bot). The link completes
 * server-side asynchronously, so the FE shows the code + countdown and polls
 * `listChannels` until a `teams` channel appears.
 */
export interface TeamsLinkCode {
  code: string;
  expires_at: string;
  ttl_seconds: number;
  instructions: string;
}

// ---------- internal helpers ----------

function getAuthHeader(token?: string): Record<string, string> {
  const t = token ?? store.getState().auth.agency.accessToken;
  const headers: Record<string, string> = {
    Accept: 'application/json',
  };
  if (t) {
    headers['Authorization'] = `Bearer ${t}`;
  }
  return headers;
}

async function parseJson<T>(response: Response): Promise<T> {
  const text = await response.text();
  if (!text) {
    return {} as T;
  }
  try {
    return JSON.parse(text) as T;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('[notifications-api] failed to parse JSON', err, text);
    throw new Error('Invalid response from server.');
  }
}

async function request<T>(
  path: string,
  init: RequestInit = {},
  tokenOverride?: string
): Promise<T> {
  const headers: Record<string, string> = {
    ...getAuthHeader(tokenOverride),
    ...((init.headers as Record<string, string>) || {}),
  };

  if (init.body && !headers['Content-Type']) {
    headers['Content-Type'] = 'application/json';
  }

  const response = await fetch(`${CENTRAL_API_BASE_URL}${path}`, {
    ...init,
    headers,
  });

  if (!response.ok) {
    let detail = '';
    let validation: Record<string, string[]> | undefined;
    try {
      const body = await parseJson<{
        message?: string;
        error?: string;
        errors?: Record<string, string[]>;
      }>(response);
      detail = body?.message || body?.error || '';
      validation = body?.errors;
    } catch {
      // swallow — fall back to status text
    }
    const err = new Error(
      detail || `Request failed (${response.status} ${response.statusText})`
    ) as Error & {
      status?: number;
      validation?: Record<string, string[]>;
    };
    err.status = response.status;
    if (validation) err.validation = validation;
    throw err;
  }

  return parseJson<T>(response);
}

function unwrap<T>(raw: T | { data?: T }): T {
  if (
    raw &&
    typeof raw === 'object' &&
    'data' in (raw as Record<string, unknown>) &&
    (raw as { data?: T }).data !== undefined
  ) {
    return (raw as { data: T }).data;
  }
  return raw as T;
}

function shopPath(slug: string): string {
  return `shops/${encodeURIComponent(slug)}/notifications`;
}

// ---------- public API ----------

/** List configured notification channels for a shop. */
export async function listChannels(
  slug: string,
  token?: string
): Promise<NotificationChannel[]> {
  const raw = await request<
    | NotificationChannel[]
    | { data?: NotificationChannel[]; channels?: NotificationChannel[] }
  >(shopPath(slug), { method: 'GET' }, token);
  if (Array.isArray(raw)) return raw;
  if (raw && 'channels' in raw && Array.isArray(raw.channels)) {
    return raw.channels;
  }
  return (
    unwrap<NotificationChannel[]>(raw as { data?: NotificationChannel[] }) ?? []
  );
}

/**
 * Fetch the install URL (or instructions URL) for a given platform.
 *
 * Slack returns `{ install_url, manual: false }` — the FE opens that URL
 * in a new tab and polls `listChannels` to detect when the OAuth landed.
 *
 * Teams / GChat / Email return `{ instructions_url, manual: true }` —
 * the FE displays the instructions and shows the paste-in form.
 */
export async function getInstallUrl(
  slug: string,
  platform: ChannelType,
  token?: string
): Promise<InstallUrlResponse> {
  return request<InstallUrlResponse>(
    `${shopPath(slug)}/${encodeURIComponent(platform)}/install-url`,
    { method: 'GET' },
    token
  );
}

/**
 * Submit a manual paste-in install for Teams / GChat / Email.
 *
 * Body shape per platform:
 *   teams  -> { app_id, app_password, tenant_id, team_name }
 *   gchat  -> { service_account_json, workspace_id?, space_id }
 *   email  -> { resend_api_key, sender_email, sender_name, recipients[] }
 *
 * The backend validates and persists; secrets in the response are stripped.
 */
export async function installManual(
  slug: string,
  platform: ChannelType,
  body: Record<string, unknown>,
  token?: string
): Promise<NotificationChannel> {
  const raw = await request<
    NotificationChannel | { data?: NotificationChannel }
  >(
    `${shopPath(slug)}/${encodeURIComponent(platform)}/manual`,
    {
      method: 'POST',
      body: JSON.stringify(body),
    },
    token
  );
  return unwrap<NotificationChannel>(raw);
}

/**
 * Google Chat — manual connect via the single shared ADUP service-account bot.
 *
 * `POST /notifications/gchat/manual` with `{ space_id }`. The backend accepts
 * either a fully-qualified `spaces/AAAA` id or a bare `AAAA` id. On success the
 * channel row is returned (now connected). On failure the API responds `502`
 * with `error: 'gateway_install_failed' | 'gchat_verification_failed' |
 * 'gateway_unreachable'` — meaning the bot can't reach that space (it was never
 * added to it). The caller surfaces that as an inline "add the ADUP app" hint.
 */
export async function connectGoogleChat(
  slug: string,
  spaceId: string,
  token?: string
): Promise<NotificationChannel> {
  const raw = await request<
    NotificationChannel | { data?: NotificationChannel }
  >(
    `${shopPath(slug)}/gchat/manual`,
    {
      method: 'POST',
      body: JSON.stringify({ space_id: spaceId }),
    },
    token
  );
  return unwrap<NotificationChannel>(raw);
}

/**
 * Microsoft Teams — request a short-lived connection code.
 *
 * `POST /notifications/teams/link-code` (no body) → `{ data: { code,
 * expires_at, ttl_seconds, instructions } }`. The agency adds the ADUP bot to
 * a Teams channel, @mentions it, and pastes this code; the link completes
 * server-side asynchronously. The caller shows the code + countdown and polls
 * `listChannels` until a `teams` channel appears.
 */
export async function generateTeamsCode(
  slug: string,
  token?: string
): Promise<TeamsLinkCode> {
  const raw = await request<TeamsLinkCode | { data?: TeamsLinkCode }>(
    `${shopPath(slug)}/teams/link-code`,
    { method: 'POST' },
    token
  );
  return unwrap<TeamsLinkCode>(raw);
}

/** Disconnect (delete) a notification channel by id. */
export async function disconnectChannel(
  slug: string,
  id: number,
  token?: string
): Promise<void> {
  await request<unknown>(
    `${shopPath(slug)}/${encodeURIComponent(String(id))}`,
    { method: 'DELETE' },
    token
  );
}

/** Send a test message through a specific channel. */
export async function testChannel(
  slug: string,
  id: number,
  token?: string
): Promise<TestResult> {
  const raw = await request<TestResult | { data?: TestResult }>(
    `${shopPath(slug)}/${encodeURIComponent(String(id))}/test`,
    { method: 'POST' },
    token
  );
  return unwrap<TestResult>(raw);
}

/**
 * Phase 10-C — update mutable fields on a channel.
 *
 * PUT /api/v1/shops/{slug}/notifications/{id}
 *
 * Accepts a partial patch — only changed fields need to be sent. The backend
 * validates `notification_mode` against the enum, clamps
 * `digest_interval_hours` to 1-168, and `digest_min_items` to 1-50.
 *
 * Secrets in `config` cannot be edited here — disconnect + reconnect to rotate
 * credentials.
 */
export async function updateChannel(
  slug: string,
  id: number,
  patch: Partial<
    Pick<
      NotificationChannel,
      | 'notification_mode'
      | 'digest_interval_hours'
      | 'digest_min_items'
      | 'event_filter'
      | 'is_active'
    >
  >,
  token?: string
): Promise<NotificationChannel> {
  const raw = await request<
    NotificationChannel | { data?: NotificationChannel }
  >(
    `${shopPath(slug)}/${encodeURIComponent(String(id))}`,
    {
      method: 'PUT',
      body: JSON.stringify(patch),
    },
    token
  );
  return unwrap<NotificationChannel>(raw);
}
